'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    console.log('Login page hydrated successfully');
  }, []);

  const doLogin = async (emailVal: string, passwordVal: string) => {
    console.log('Attempting login with:', { email: emailVal, password: '***' });
    setError('');
    setLoading(true);

    try {
      console.log('Calling signIn...');
      const result = await signIn('credentials', {
        email: emailVal,
        password: passwordVal,
        loginPath: 'office',
        redirect: false,
      });
      console.log('signIn result:', result);

      if (result?.error) {
        console.error('Login error:', result.error);
        setError('Invalid email or password');
        setLoading(false);
      } else {
        console.log('Login successful, redirecting...');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Login exception:', err);
      setError('An error occurred. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form onSubmit triggered');

    // Get values directly from form elements to handle browser autofill
    const form = formRef.current;
    if (form) {
      const emailInput = form.querySelector<HTMLInputElement>('#email');
      const passwordInput = form.querySelector<HTMLInputElement>('#password');
      const emailVal = emailInput?.value || email;
      const passwordVal = passwordInput?.value || password;
      await doLogin(emailVal, passwordVal);
    } else {
      await doLogin(email, password);
    }
  };

  const handleButtonClick = () => {
    console.log('Button clicked directly');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">ForgeFlow</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  href="/forgot-password"
                  className="text-xs text-indigo-600 hover:underline"
                  tabIndex={-1}
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="w-full h-9 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
              disabled={loading}
              onClick={handleButtonClick}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
